// ---------------------------------------------------------------------------
// Avisos push (Fase 1 do app). A tela só ASSINA; quem envia é o banco.
// ---------------------------------------------------------------------------

import { supabase } from './supabase';
import { ehIOS, ehAppInstalado } from './navegador';

export type PapelAviso = 'comprador' | 'organizador';

export function suportaAvisos(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// iOS só recebe aviso com o app na tela inicial (16.4+).
export function precisaInstalarNoIphone(): boolean {
  return ehIOS && !ehAppInstalado;
}

export function permissaoNegada(): boolean {
  return 'Notification' in window && Notification.permission === 'denied';
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function obterAssinatura(): Promise<PushSubscription | null> {
  if (!suportaAvisos()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export interface LigarAvisosParams {
  evento: number;
  carteira?: string | null;
  papel: PapelAviso;
}

// Pede permissão (só depois do toque) e assina o evento. Idempotente: se já
// existe uma assinatura Push neste aparelho, reusa e só registra o vínculo.
export async function ligarAvisos({ evento, carteira, papel }: LigarAvisosParams): Promise<void> {
  if (!suportaAvisos()) throw new Error('Este navegador não recebe avisos.');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error('Sem permissão. Você pode ligar depois nas configurações do navegador.');
  }

  const reg = await navigator.serviceWorker.ready;

  const { data, error: errChave } = await supabase.functions.invoke('notificar?acao=chave');
  if (errChave) throw new Error('Não foi possível ligar os avisos agora.');
  const chavePublica = (data as { chave_publica?: string } | null)?.chave_publica;
  if (!chavePublica) throw new Error('Não foi possível ligar os avisos agora.');

  const chave = urlBase64ToUint8Array(chavePublica);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chave,
    });
  }

  const j = sub.toJSON();
  const { error: errAssinar } = await supabase.rpc('push_assinar', {
    p_endpoint: sub.endpoint,
    p_p256dh: j.keys?.p256dh ?? null,
    p_auth: j.keys?.auth ?? null,
    p_aparelho: navigator.userAgent.slice(0, 200),
    p_evento: evento,
    p_carteira: carteira ?? null,
    p_papel: papel,
  });
  if (errAssinar) throw errAssinar;
}

// Desliga os avisos de um evento (ou de todos, se nenhum for informado).
export async function desligarAvisos(evento?: number): Promise<void> {
  const sub = await obterAssinatura();
  if (!sub) return;
  await supabase.rpc('push_cancelar', {
    p_endpoint: sub.endpoint,
    p_evento: evento ?? null,
    p_papel: null,
  });
}

export interface EventoAviso {
  evento_id: number;
  papel: string;
}

export interface SituacaoAviso {
  ativa: boolean;
  eventos: EventoAviso[];
}

export async function situacaoAvisos(): Promise<SituacaoAviso> {
  if (!suportaAvisos()) return { ativa: false, eventos: [] };
  try {
    const sub = await obterAssinatura();
    if (!sub) return { ativa: false, eventos: [] };
    const { data, error } = await supabase.rpc('push_situacao', { p_endpoint: sub.endpoint });
    if (error) return { ativa: false, eventos: [] };
    const d = (Array.isArray(data) ? data[0] : data) as SituacaoAviso | null;
    return d ?? { ativa: false, eventos: [] };
  } catch {
    return { ativa: false, eventos: [] };
  }
}

export function estaLigadoPara(situacao: SituacaoAviso, evento: number): boolean {
  return situacao.ativa && situacao.eventos.some((e) => e.evento_id === evento);
}

// ---------------------------------------------------------------------------
// Avisos gerais do app (sem evento): usados no convite "Ative os avisos do
// Se Liga" e na reassinatura silenciosa. A assinatura fica presa à pessoa.
// ---------------------------------------------------------------------------

export type EstadoPermissao = 'unsupported' | 'default' | 'granted' | 'denied';

export function estadoPermissao(): EstadoPermissao {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as EstadoPermissao;
}

async function obterChavePublica(): Promise<string> {
  const { data, error } = await supabase.functions.invoke('notificar?acao=chave');
  if (error) throw new Error('Não foi possível ligar os avisos agora.');
  const chave = (data as { chave_publica?: string } | null)?.chave_publica;
  if (!chave) throw new Error('Não foi possível ligar os avisos agora.');
  return chave;
}

// Cria (ou reaproveita) a assinatura no aparelho e a registra no banco, sem
// vínculo com evento — é a assinatura "geral" da pessoa.
async function registrarAssinatura(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  const { error } = await supabase.rpc('push_assinar', {
    p_endpoint: sub.endpoint,
    p_p256dh: j.keys?.p256dh ?? null,
    p_auth: j.keys?.auth ?? null,
    p_aparelho: navigator.userAgent.slice(0, 200),
  });
  if (error) throw error;
}

// Pede a permissão (só depois do toque) e liga os avisos gerais. Idempotente.
export async function ligarAvisosGerais(): Promise<void> {
  if (!suportaAvisos()) throw new Error('Este navegador não recebe avisos.');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error('Sem permissão. Você pode ligar depois nas configurações do celular.');
  }

  const reg = await navigator.serviceWorker.ready;
  const chave = urlBase64ToUint8Array(await obterChavePublica());

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: chave,
    });
  }
  await registrarAssinatura(sub);
}

// Quando a permissão já está dada, reafirma a assinatura sem pedir nada.
// Mantém o vínculo aparelho ↔ pessoa sempre vivo a cada abertura do app.
export async function reassinarEmSilencio(): Promise<boolean> {
  if (!suportaAvisos() || estadoPermissao() !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const chave = urlBase64ToUint8Array(await obterChavePublica());
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: chave,
      });
    }
    await registrarAssinatura(sub);
    return true;
  } catch {
    return false;
  }
}

export interface TesteAvisoResultado {
  ok: boolean;
  erro?: string;
  aparelhos?: number;
}

// Dispara um aviso de teste para os aparelhos da própria pessoa.
export async function testarAviso(): Promise<TesteAvisoResultado> {
  const { data, error } = await supabase.rpc('aviso_teste_para_mim');
  if (error) throw error;
  const d = data as { ok?: boolean; erro?: string; aparelhos?: number } | null;
  return { ok: d?.ok === true, erro: d?.erro, aparelhos: d?.aparelhos };
}

// ---------------------------------------------------------------------------
// Convite "Ative os avisos": sem soneca — o cartão reaparece a cada abertura do
// app enquanto a permissão não estiver liberada (o "Agora não" fecha só a atual).
// ---------------------------------------------------------------------------